import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    CardActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup,
    Collapse,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Slider
} from "@mui/material";
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterListIcon from "@mui/icons-material/FilterList";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleIcon from "@mui/icons-material/People";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SortIcon from "@mui/icons-material/Sort";

import useAxios from "../../../api/useAxios";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const AdminAllJobs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const api = useAxios();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [viewMode, setViewMode] = useState("card"); // 'table' or 'card'
    const [showFilters, setShowFilters] = useState(!isMobile);

    // View Details State
    const [selectedJob, setSelectedJob] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    // Filters State
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("most_recent");
    const [filters, setFilters] = useState({
        company: "",
        location: "",
        industry: "",
        date: "",
        experience_range: [0, 15], // Changed to range array [min, max]
        job_status: "",
        need_type: ""
    });

    // Accordion Expanded States
    const [expandedAccordion, setExpandedAccordion] = useState({
        location: true,
        industry: true,
        experience: true,
        status: true
    });

    const themeColor = "#339fcd"; // Brand color from AdminSidebar

    // Stats State
    const [stats, setStats] = useState({
        total: 0,
        today: 0,
        active: 0,
        urgent: 0
    });

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page + 1);
            params.append('limit', rowsPerPage);

            if (search) params.append('search', search);
            if (filters.company) params.append('company', filters.company);
            if (filters.location) params.append('location', filters.location);
            if (filters.industry) params.append('industry', filters.industry);
            if (filters.job_status) params.append('job_status', filters.job_status);
            if (filters.need_type) params.append('need_type', filters.need_type);

            // Experience Range Params
            if (filters.experience_range && filters.experience_range.length === 2) {
                params.append('min_exp', filters.experience_range[0]);
                params.append('max_exp', filters.experience_range[1]);
            }

            if (filters.date) {
                // Formatting date to YYYY-MM-DD if needed, though input type="date" usually gives this format
                params.append('date', filters.date);
            }

            if (sortBy === 'openings_high') params.append('sort_by', 'openings_high');
            if (sortBy === 'openings_low') params.append('sort_by', 'openings_low');
            if (sortBy === 'most_recent') params.append('sort_by', 'most_recent');

            const response = await api.get(`jobs/admin/all?${params.toString()}`);
            if (response.data && response.data.data) {
                setJobs(response.data.data);
                setTotalCount(response.data.totalCount || 0);
                if (response.data.stats) {
                    setStats(prev => ({
                        ...prev,
                        ...response.data.stats,
                        total: response.data.totalCount // Sync total with totalCount
                    }));
                }
            } else {
                setJobs([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error("Error fetching jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch on mount
        fetchJobs();
    }, []);

    useEffect(() => {
        // Skip debounced fetch on initial mount (optional, but good practice)
        // We'll use a timer for subsequent changes
        const delayDebounceFn = setTimeout(() => {
            fetchJobs();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [search, filters, page, rowsPerPage, sortBy]);

    // Force Card View on Mobile
    useEffect(() => {
        if (isMobile && viewMode === 'table') {
            setViewMode('card');
        }
    }, [isMobile, viewMode]);
    // Note: We might want to exclude the initial load from here by checking if it's the first render using a ref.
    // However, simple split is usually sufficient for React 18+ strict mode behavior.

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
        setPage(0);
    };

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewMode(nextView);
        }
    };

    const clearFilters = () => {
        setFilters({
            company: "",
            location: "",
            industry: "",
            date: "",
            experience_type: "",
            job_status: "",
            need_type: ""
        });
        setSortBy("most_recent");
        setSearch("");
        setPage(0);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion({ ...expandedAccordion, [panel]: isExpanded });
    };

    const handleViewDetails = (job) => {
        setSelectedJob(job);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedJob(null);
    };

    const handleBack = () => {
        setSelectedJob(null);
    };

    return (
        <Box sx={{ p: { xs: 0, sm: 2, md: 4 }, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            {/* Full Page Job Details View - Hero Redesign */}
            {selectedJob ? (
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: '85vh',
                    backgroundColor: "#f8fafc",
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Hero Banner with Gradient Overlay - Matches Card Theme */}
                    <Box sx={{
                        position: 'relative',
                        height: { xs: '340px', sm: '260px', md: '280px' },
                        background: selectedJob.need_type === "Urgent"
                            ? 'linear-gradient(90deg, #ef4444 0%, #f97316 50%, #eab308 100%)'
                            : `linear-gradient(90deg, ${themeColor} 0%, #5bbce4 50%, #a78bfa 100%)`,
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, transparent 100%)'
                        }
                    }}>
                        {/* Abstract Background Pattern */}
                        <Box sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                            backgroundSize: '500px 500px'
                        }} />

                        {/* Content Container */}
                        <Box sx={{
                            position: 'relative',
                            height: '100%',
                            maxWidth: '1400px',
                            mx: 'auto',
                            px: { xs: 2, md: 4 },
                            py: { xs: 2, md: 3 },
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header Bar */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'flex-start', sm: 'flex-start' },
                                gap: { xs: 1, sm: 0 },
                                mb: { xs: 1.5, md: 3 }
                            }}>
                                <Button
                                    onClick={handleBack}
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        color: "#fff",
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backdropFilter: "blur(8px)",
                                        backgroundColor: "rgba(255,255,255,0.15)",
                                        borderRadius: "10px",
                                        px: 2,
                                        py: 0.75,
                                        border: "1px solid rgba(255,255,255,0.2)",
                                        '&:hover': {
                                            backgroundColor: "rgba(255,255,255,0.25)",
                                            transform: "translateY(-1px)"
                                        },
                                        transition: "all 0.2s",
                                        fontSize: { xs: '0.875rem', md: '1rem' }
                                    }}
                                >
                                    Back
                                </Button>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={selectedJob.job_status}
                                        sx={{
                                            borderRadius: "10px",
                                            height: "30px",
                                            px: 1.5,
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            background: selectedJob.job_status === "Active"
                                                ? "linear-gradient(135deg, #10b981 0%, #34d399 100%)"
                                                : "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
                                            color: "#fff",
                                            boxShadow: selectedJob.job_status === "Active"
                                                ? "0 4px 12px rgba(16, 185, 129, 0.4)"
                                                : "0 4px 12px rgba(239, 68, 68, 0.4)",
                                            border: "none",
                                            backdropFilter: "none"
                                        }}
                                    />
                                    {selectedJob.need_type === "Urgent" && (
                                        <Chip
                                            icon={<LocalFireDepartmentIcon sx={{ fontSize: '16px !important', color: '#b45309 !important' }} />}
                                            label="URGENT"
                                            sx={{
                                                borderRadius: "10px",
                                                height: "30px",
                                                px: 1.5,
                                                fontSize: "0.7rem",
                                                fontWeight: 800,
                                                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                                color: "#b45309",
                                                border: "1px solid #fcd34d",
                                                boxShadow: "none",
                                                '& .MuiChip-icon': { ml: 0 }
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {/* Job Title and Details */}
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                color: "#fff",
                                mt: { xs: 0.5, md: 0 },
                                width: '100%',
                                maxWidth: '100%',
                                overflow: 'hidden'
                            }}>
                                <Typography variant="h3" sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.25rem' },
                                    lineHeight: 1.2,
                                    mb: { xs: 1.5, md: 2 },
                                    textShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                    wordBreak: 'break-word'
                                }}>
                                    {selectedJob.job_title}
                                </Typography>

                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: { xs: 1, md: 2.5 },
                                    alignItems: 'center',
                                    mb: { xs: 1.5, md: 2.5 },
                                    width: '100%'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 0.75,
                                        pr: 1.5,
                                        borderRadius: "12px",
                                        backgroundColor: "rgba(255,255,255,0.15)",
                                        backdropFilter: "blur(4px)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        maxWidth: { xs: '100%', sm: 'auto' }
                                    }}>
                                        <Box sx={{
                                            width: { xs: 24, md: 32 },
                                            height: { xs: 24, md: 32 },
                                            borderRadius: "8px",
                                            backgroundColor: "rgba(255,255,255,0.2)",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <BusinessIcon sx={{ fontSize: { xs: 14, md: 18 }, color: "#fff" }} />
                                        </Box>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: "#fff",
                                            fontSize: { xs: '0.7rem', sm: '0.85rem', md: '1rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: { xs: 'nowrap', sm: 'normal' }
                                        }}>
                                            {selectedJob.company_name}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 0.75,
                                        pr: 1.5,
                                        borderRadius: "12px",
                                        backgroundColor: "rgba(255,255,255,0.15)",
                                        backdropFilter: "blur(4px)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        maxWidth: { xs: '100%', sm: 'auto' }
                                    }}>
                                        <Box sx={{
                                            width: { xs: 24, md: 32 },
                                            height: { xs: 24, md: 32 },
                                            borderRadius: "8px",
                                            backgroundColor: "rgba(255,255,255,0.2)",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <LocationOnIcon sx={{ fontSize: { xs: 14, md: 18 }, color: "#fff" }} />
                                        </Box>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: "#fff",
                                            fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1.05rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: { xs: 'nowrap', sm: 'normal' }
                                        }}>
                                            {selectedJob.job_location}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Quick Stats */}
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: { xs: 1, md: 1.5 },
                                    mt: { xs: 0.5, md: 1.5 }
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        px: { xs: 1.25, md: 1.5 },
                                        py: { xs: 0.5, md: 0.75 },
                                        borderRadius: '8px',
                                        backdropFilter: 'blur(8px)',
                                        border: "1px solid rgba(255,255,255,0.1)"
                                    }}>
                                        <PeopleIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                                            {selectedJob.applicantCount || 0} Applicants
                                        </Typography>
                                    </Box>

                                    {selectedJob.number_of_positions && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            px: { xs: 1.25, md: 1.5 },
                                            py: { xs: 0.5, md: 0.75 },
                                            borderRadius: '8px',
                                            backdropFilter: 'blur(8px)',
                                            border: "1px solid rgba(255,255,255,0.1)"
                                        }}>
                                            <WorkIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                                                {selectedJob.number_of_positions} Openings
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedJob.salary_range_min && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            px: { xs: 1.25, md: 1.5 },
                                            py: { xs: 0.5, md: 0.75 },
                                            borderRadius: '8px',
                                            backdropFilter: 'blur(8px)',
                                            border: "1px solid rgba(255,255,255,0.1)"
                                        }}>
                                            <AttachMoneyIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                                                ₹{selectedJob.salary_range_min} - ₹{selectedJob.salary_range_max}
                                            </Typography>
                                        </Box>
                                    )}

                                    {selectedJob.experience_min && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            px: { xs: 1.25, md: 1.5 },
                                            py: { xs: 0.5, md: 0.75 },
                                            borderRadius: '8px',
                                            backdropFilter: 'blur(8px)',
                                            border: "1px solid rgba(255,255,255,0.1)"
                                        }}>
                                            <WorkIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                                                {selectedJob.experience_min} - {selectedJob.experience_max} Years
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Main Content Area */}
                    <Box sx={{
                        flex: 1,
                        maxWidth: '1400px',
                        mx: 'auto',
                        width: '100%',
                        px: { xs: 2, sm: 3, md: 4 },
                        py: { xs: 2, md: 3.5 }
                    }}>
                        <Grid container spacing={3}>
                            {/* Left Column - Job Details */}
                            <Grid item xs={12} lg={8}>
                                {/* Description Card */}
                                <Card sx={{
                                    borderRadius: '16px',
                                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                                    mb: 3,
                                    border: '1px solid #f1f5f9',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Top Gradient Accent - Matches Card Theme */}
                                    <Box sx={{
                                        height: '4px',
                                        background: selectedJob.need_type === "Urgent"
                                            ? 'linear-gradient(90deg, #ef4444 0%, #f97316 50%, #eab308 100%)'
                                            : `linear-gradient(90deg, ${themeColor} 0%, #5bbce4 50%, #a78bfa 100%)`
                                    }} />

                                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            mb: 3,
                                            pb: 2,
                                            borderBottom: '1px solid #f1f5f9'
                                        }}>
                                            <Box sx={{
                                                width: 4,
                                                height: 24,
                                                background: `linear-gradient(180deg, ${themeColor} 0%, #4f46e5 100%)`,
                                                borderRadius: "2px"
                                            }} />
                                            <Typography variant="h5" sx={{
                                                fontWeight: 700,
                                                color: "#1e293b",
                                                fontSize: { xs: '1.25rem', md: '1.5rem' }
                                            }}>
                                                Job Description
                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            color: "#475569",
                                            lineHeight: 1.7,
                                            fontSize: { xs: '0.95rem', md: '1rem' },
                                            '& p': { mb: 2 },
                                            '& ul': { pl: 2, mb: 2 },
                                            '& li': { mb: 1, pl: 0.5 },
                                            '& strong': { color: "#1e293b", fontWeight: 700 }
                                        }} dangerouslySetInnerHTML={{ __html: selectedJob.job_description || "No description provided." }} />
                                    </CardContent>
                                </Card>

                                {/* Skills Card */}
                                {selectedJob.key_skills && (
                                    <Card sx={{
                                        borderRadius: '16px',
                                        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                                        border: '1px solid #f1f5f9'
                                    }}>
                                        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
                                            <Typography variant="h6" sx={{
                                                fontWeight: 700,
                                                color: "#1e293b",
                                                mb: 2.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                fontSize: { xs: '1.1rem', md: '1.25rem' }
                                            }}>
                                                <StarIcon sx={{ color: themeColor, fontSize: { xs: 20, md: 22 } }} />
                                                Required Skills
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedJob.key_skills.split(",").map((skill, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={skill.trim()}
                                                        size="small"
                                                        sx={{
                                                            borderRadius: "10px",
                                                            backgroundColor: "#f8fafc",
                                                            color: "#334155",
                                                            fontWeight: 600,
                                                            border: "1px solid #e2e8f0",
                                                            height: "34px",
                                                            px: 2,
                                                            fontSize: { xs: '0.8rem', md: '0.85rem' },
                                                            transition: "all 0.2s",
                                                            '&:hover': {
                                                                backgroundColor: themeColor + '15',
                                                                borderColor: themeColor,
                                                                color: themeColor,
                                                                transform: "translateY(-1px)"
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </Grid>

                            {/* Right Column - Sidebar */}
                            <Grid item xs={12} lg={4}>
                                <Box sx={{ position: 'sticky', top: 20 }}>
                                    {/* Quick Info Card */}
                                    <Card sx={{
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                                        border: '1px solid #f1f5f9',
                                        mb: 3,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Top Gradient Accent - Matches Card Theme */}
                                        <Box sx={{
                                            height: '4px',
                                            background: selectedJob.need_type === "Urgent"
                                                ? 'linear-gradient(90deg, #ef4444 0%, #f97316 50%, #eab308 100%)'
                                                : `linear-gradient(90deg, ${themeColor} 0%, #5bbce4 50%, #a78bfa 100%)`
                                        }} />

                                        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3 } }}>
                                            <Typography variant="overline" sx={{
                                                fontWeight: 700,
                                                color: "#64748b",
                                                letterSpacing: 0.5,
                                                fontSize: '0.7rem',
                                                display: 'block',
                                                mb: 2.5
                                            }}>
                                                JOB DETAILS
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {[
                                                    {
                                                        label: "Job ID",
                                                        value: selectedJob.unique_code || selectedJob.job_id,
                                                        icon: <FingerprintIcon />,
                                                        color: "#8b5cf6"
                                                    },
                                                    {
                                                        label: "Experience",
                                                        value: selectedJob.experience_type?.toLowerCase().includes("fresher")
                                                            ? selectedJob.experience_type
                                                            : `${selectedJob.experience_min || 0} - ${selectedJob.experience_max || 0} Years`,
                                                        icon: <WorkHistoryIcon />,
                                                        color: "#10b981"
                                                    },
                                                    {
                                                        label: "Posted Date",
                                                        value: selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleDateString('en-IN', {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            year: 'numeric'
                                                        }) : "Recently",
                                                        icon: <CalendarTodayIcon />,
                                                        color: "#f59e0b"
                                                    },
                                                    {
                                                        label: "Salary",
                                                        value: selectedJob.salary_range_min
                                                            ? `₹${selectedJob.salary_range_min} - ₹${selectedJob.salary_range_max}`
                                                            : "Not Disclosed",
                                                        icon: <AttachMoneyIcon />,
                                                        color: "#3b82f6"
                                                    },
                                                    {
                                                        label: "Job Type",
                                                        value: selectedJob.job_type || "Full-time",
                                                        icon: <WorkIcon />,
                                                        color: "#ec4899"
                                                    },
                                                ].map((item, idx) => (
                                                    <Box key={idx} sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        p: 1.5,
                                                        borderRadius: '12px',
                                                        backgroundColor: '#f8fafc',
                                                        border: '1px solid #f1f5f9',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#ffffff',
                                                            borderColor: '#e2e8f0',
                                                            transform: 'translateX(2px)'
                                                        }
                                                    }}>
                                                        <Box sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "10px",
                                                            backgroundColor: item.color + '15',
                                                            color: item.color,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            {React.cloneElement(item.icon, { fontSize: "small" })}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="caption" sx={{
                                                                color: "#64748b",
                                                                fontWeight: 600,
                                                                fontSize: '0.65rem',
                                                                display: 'block',
                                                                mb: 0.25
                                                            }}>
                                                                {item.label}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: 700,
                                                                color: "#1e293b",
                                                                fontSize: '0.9rem',
                                                                wordBreak: 'break-word'
                                                            }}>
                                                                {item.value}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>

                                            {/* Contact Info */}
                                            <Box sx={{ mt: 3.5, pt: 3, borderTop: '1px solid #f1f5f9' }}>
                                                <Typography variant="subtitle2" sx={{
                                                    fontWeight: 700,
                                                    color: "#64748b",
                                                    mb: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    fontSize: '0.8rem'
                                                }}>
                                                    <ContactMailIcon sx={{ fontSize: 16 }} />
                                                    CONTACT INFORMATION
                                                </Typography>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1.25,
                                                        p: 1.5,
                                                        borderRadius: '10px',
                                                        backgroundColor: '#f8fafc',
                                                        border: '1px solid #f1f5f9'
                                                    }}>
                                                        <EmailIcon sx={{
                                                            color: "#64748b",
                                                            fontSize: 18,
                                                            mt: 0.25,
                                                            flexShrink: 0
                                                        }} />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{
                                                                color: "#94a3b8",
                                                                fontWeight: 600,
                                                                fontSize: '0.65rem'
                                                            }}>
                                                                Email
                                                            </Typography>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: 600,
                                                                color: "#334155",
                                                                wordBreak: 'break-all',
                                                                fontSize: '0.85rem'
                                                            }}>
                                                                {selectedJob.contact_email_id || "Not provided"}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1.25,
                                                        p: 1.5,
                                                        borderRadius: '10px',
                                                        backgroundColor: '#f8fafc',
                                                        border: '1px solid #f1f5f9'
                                                    }}>
                                                        <PhoneIcon sx={{
                                                            color: "#64748b",
                                                            fontSize: 18,
                                                            mt: 0.25,
                                                            flexShrink: 0
                                                        }} />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{
                                                                color: "#94a3b8",
                                                                fontWeight: 600,
                                                                fontSize: '0.65rem'
                                                            }}>
                                                                Phone
                                                            </Typography>
                                                            <Typography variant="body2" sx={{
                                                                fontWeight: 600,
                                                                color: "#334155",
                                                                fontSize: '0.85rem'
                                                            }}>
                                                                {selectedJob.phone_number || "Not provided"}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Box>
                        {/* Header Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                mb: 4,
                                borderRadius: "16px",
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: { xs: 2, sm: 0 },
                                background: "linear-gradient(135deg, #fff 0%, #f1f8ff 100%)",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
                            }}
                        >
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: "800", color: "#1e293b", mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                                    All Jobs
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    Manage and oversee all job listings
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={handleViewChange}
                                    aria-label="view mode"
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', md: 'flex' }, // Hide on mobile
                                        backgroundColor: "white",
                                        '& .MuiToggleButton-root.Mui-selected': {
                                            backgroundColor: themeColor + "20",
                                            color: themeColor
                                        }
                                    }}
                                >
                                    <ToggleButton value="card" aria-label="card view">
                                        <Tooltip title="Grid View"><ViewModuleIcon /></Tooltip>
                                    </ToggleButton>
                                    <ToggleButton value="table" aria-label="table view">
                                        <Tooltip title="List View"><ViewListIcon /></Tooltip>
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <Button
                                    startIcon={showFilters ? <CloseIcon /> : <FilterListIcon />}
                                    onClick={() => setShowFilters(!showFilters)}
                                    variant={showFilters ? "contained" : "outlined"}
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        borderColor: showFilters ? "transparent" : themeColor,
                                        backgroundColor: showFilters ? themeColor : "transparent",
                                        color: showFilters ? "white" : themeColor,
                                        // Display on all screens (previously hidden on desktop)
                                        flex: { xs: 1, sm: 'none' },
                                        '&:hover': {
                                            backgroundColor: showFilters ? "#287fa4" : themeColor + "10",
                                            borderColor: themeColor
                                        }
                                    }}
                                >
                                    {showFilters ? "Close" : "Filters"}
                                </Button>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchJobs}
                                    variant="contained"
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        backgroundColor: themeColor,
                                        flex: { xs: 1, sm: 'none' },
                                        '&:hover': { backgroundColor: "#287fa4" }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Paper>

                        {/* Stats Widgets */}
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {[
                                { title: "Total Jobs", count: totalCount, icon: <WorkIcon />, color: "#339fcd" },
                                { title: "Today's Jobs", count: stats.today, icon: <CalendarTodayIcon />, color: "#4caf50" },
                                { title: "Active Jobs", count: stats.active, icon: <BusinessIcon />, color: "#ff9800" },
                                { title: "Urgent Jobs", count: stats.urgent, icon: <LocationOnIcon />, color: "#e91e63" }, // Vibrant pink/red
                                { title: "Total Applied", count: stats.totalApplied, icon: <PeopleIcon />, color: "#673ab7" }
                            ].map((stat, index) => (
                                <Grid item xs={12} sm={6} md={2.4} key={index} sx={{ flexBasis: { md: '20%' }, maxWidth: { md: '20%' } }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: "20px",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}bb 100%)`,
                                            color: 'white',
                                            boxShadow: `0 10px 20px -5px ${stat.color}44`,
                                            height: '100%',
                                            transition: "all 0.3s ease",
                                            cursor: "default",
                                            '&:hover': {
                                                transform: "translateY(-5px)",
                                                boxShadow: `0 15px 30px -5px ${stat.color}66`,
                                            }
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.9, fontSize: '0.65rem' }}>{stat.title}</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: -0.5 }}>{stat.count}</Typography>
                                        </Box>
                                        <Box sx={{
                                            p: 1.5,
                                            borderRadius: "14px",
                                            backgroundColor: "rgba(255,255,255,0.25)",
                                            color: "white",
                                            display: 'flex',
                                            backdropFilter: "blur(4px)"
                                        }}>
                                            {React.cloneElement(stat.icon, { fontSize: 'medium' })}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Main Layout with Filter Sidebar and Jobs Content */}
                        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                            {/* Left Filter Sidebar */}
                            <Paper
                                elevation={0}
                                sx={{
                                    width: { xs: '100%', md: '280px' },
                                    flexShrink: 0,
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: '#fff',
                                    height: 'fit-content',
                                    position: { md: 'sticky' },
                                    top: { md: 20 },
                                    display: showFilters ? 'block' : 'none'
                                }}
                            >
                                {/* Filters Header */}
                                <Box sx={{
                                    p: 2.5,
                                    borderBottom: '1px solid #f1f5f9',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                        Filters
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={clearFilters}
                                        sx={{
                                            textTransform: 'none',
                                            color: themeColor,
                                            fontWeight: 600,
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        Clear All
                                    </Button>
                                </Box>

                                {/* Company Filter */}
                                <Accordion
                                    expanded={expandedAccordion.company}
                                    onChange={handleAccordionChange('company')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Company
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter company..."
                                            value={filters.company}
                                            onChange={handleFilterChange('company')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#f8fafc'
                                                }
                                            }}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Location Filter */}
                                <Accordion
                                    expanded={expandedAccordion.location}
                                    onChange={handleAccordionChange('location')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Location
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter location..."
                                            value={filters.location}
                                            onChange={handleFilterChange('location')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#f8fafc'
                                                }
                                            }}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Job Category Filter */}
                                <Accordion
                                    expanded={expandedAccordion.industry}
                                    onChange={handleAccordionChange('industry')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Job Category
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter category..."
                                            value={filters.industry}
                                            onChange={handleFilterChange('industry')}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#f8fafc'
                                                }
                                            }}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Experience Filter */}
                                <Accordion
                                    expanded={expandedAccordion.experience}
                                    onChange={handleAccordionChange('experience')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Experience
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={filters.experience_range[0] === 0 && filters.experience_range[1] === 0}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        // If checked, set range to [0, 0] (Strictly Fresher)
                                                        // If unchecked, set range to default [0, 15] or just expand slightly? Default is better.
                                                        setFilters({ ...filters, experience_range: isChecked ? [0, 0] : [0, 15] });
                                                    }}
                                                    sx={{
                                                        color: '#e2e8f0',
                                                        '&.Mui-checked': {
                                                            color: themeColor,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={<Typography sx={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>I am a Fresher</Typography>}
                                        />
                                        <Box sx={{ px: 1, mt: 1 }}>
                                            <Slider
                                                value={filters.experience_range}
                                                onChange={(e, newValue) => setFilters({ ...filters, experience_range: newValue })}
                                                min={0}
                                                max={15}
                                                step={0.5} // Allow 0.5 year increments
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(value) => value === 0 ? "Fresher" : `${value} Yrs`}
                                                disableSwap
                                                marks={[
                                                    { value: 0, label: 'Fresher' },
                                                    { value: 5, label: '5' },
                                                    { value: 10, label: '10' },
                                                    { value: 15, label: '15+' }
                                                ]}
                                                sx={{
                                                    color: themeColor,
                                                    height: 6,
                                                    '& .MuiSlider-thumb': {
                                                        height: 20,
                                                        width: 20,
                                                        backgroundColor: '#fff',
                                                        border: `2px solid ${themeColor}`,
                                                        '&:focus, &:hover, &.Mui-active': {
                                                            boxShadow: 'inherit',
                                                        },
                                                    },
                                                    '& .MuiSlider-valueLabel': {
                                                        backgroundColor: themeColor,
                                                    },
                                                    '& .MuiSlider-markLabel': {
                                                        fontSize: '0.7rem',
                                                        color: '#94a3b8'
                                                    },
                                                    '& .MuiSlider-markLabel[data-index="0"]': {
                                                        fontWeight: 600,
                                                        color: themeColor,
                                                        transform: 'translateX(0)' // Align left
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Posted Date Filter */}
                                <Accordion
                                    expanded={expandedAccordion.date}
                                    onChange={handleAccordionChange('date')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' },
                                        borderBottom: '1px solid #f1f5f9'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Posted Date
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            value={filters.date}
                                            onChange={handleFilterChange('date')}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#f8fafc'
                                                }
                                            }}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Status & Priority Filters */}
                                <Accordion
                                    expanded={expandedAccordion.status}
                                    onChange={handleAccordionChange('status')}
                                    elevation={0}
                                    disableGutters
                                    sx={{
                                        '&:before': { display: 'none' }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ px: 2.5, py: 1.5 }}
                                    >
                                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                                            Status & Priority
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1.5, display: 'block' }}>
                                            Job Status
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                            {[{ value: "", label: "All" }, { value: "Active", label: "Active" }, { value: "InActive", label: "Inactive" }].map((opt) => (
                                                <Chip
                                                    key={opt.value}
                                                    label={opt.label}
                                                    size="small"
                                                    onClick={() => setFilters({ ...filters, job_status: opt.value })}
                                                    sx={{
                                                        fontWeight: 600,
                                                        borderRadius: "8px",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s",
                                                        backgroundColor: filters.job_status === opt.value ? (opt.value === "Active" ? "#10b981" : opt.value === "InActive" ? "#ef4444" : themeColor) : "#f1f5f9",
                                                        color: filters.job_status === opt.value ? "#fff" : "#64748b",
                                                        '&:hover': { opacity: 0.8 }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 1.5, display: 'block' }}>
                                            Priority
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {[{ value: "", label: "All" }, { value: "Urgent", label: "🔥 Urgent" }, { value: "Normal", label: "Normal" }].map((opt) => (
                                                <Chip
                                                    key={opt.value}
                                                    label={opt.label}
                                                    size="small"
                                                    onClick={() => setFilters({ ...filters, need_type: opt.value })}
                                                    sx={{
                                                        fontWeight: 600,
                                                        borderRadius: "8px",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s",
                                                        backgroundColor: filters.need_type === opt.value ? (opt.value === "Urgent" ? "#e11d48" : themeColor) : "#f1f5f9",
                                                        color: filters.need_type === opt.value ? "#fff" : "#64748b",
                                                        '&:hover': { opacity: 0.8 }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>

                            {/* Right Content Area */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                {/* Jobs Header with Count and Sort */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    gap: 2
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                            Jobs
                                        </Typography>
                                        <Chip
                                            label={totalCount}
                                            size="small"
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#f1f5f9',
                                                color: '#64748b',
                                                borderRadius: '6px',
                                                height: '28px'
                                            }}
                                        />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {/* Search Input */}
                                        <TextField
                                            variant="outlined"
                                            placeholder="Search jobs..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            size="small"
                                            sx={{
                                                width: { xs: '100%', sm: '250px' },
                                                backgroundColor: "white",
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    '& fieldset': { borderColor: '#e2e8f0' }
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {/* Sort By Dropdown */}
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <Select
                                                value={sortBy}
                                                onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
                                                startAdornment={
                                                    <SortIcon sx={{ color: '#64748b', mr: 1, fontSize: 20 }} />
                                                }
                                                sx={{
                                                    borderRadius: '10px',
                                                    backgroundColor: '#fff',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#e2e8f0'
                                                    },
                                                    '& .MuiSelect-select': {
                                                        fontWeight: 600,
                                                        color: '#334155'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="most_recent">
                                                    <Typography sx={{ fontWeight: filters.job_status === "most_recent" ? 700 : 400 }}>
                                                        Most Recent (Default)
                                                    </Typography>
                                                </MenuItem>
                                                <MenuItem value="openings_high_to_low">
                                                    <Typography>No. of Openings: High to Low</Typography>
                                                </MenuItem>
                                                <MenuItem value="openings_low_to_high">
                                                    <Typography>No. of Openings: Low to High</Typography>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                                        <CircularProgress sx={{ color: themeColor }} />
                                    </Box>
                                ) : (
                                    <>
                                        {viewMode === 'table' ? (
                                            <TableContainer
                                                component={Paper}
                                                elevation={0}
                                                sx={{
                                                    borderRadius: "20px",
                                                    overflow: "hidden",
                                                    border: "1px solid #e2e8f0",
                                                    boxShadow: "0 10px 40px rgba(0,0,0,0.04)"
                                                }}
                                            >
                                                <Table sx={{ minWidth: 750, tableLayout: 'fixed' }}>
                                                    <TableHead>
                                                        <TableRow sx={{
                                                            background: `linear-gradient(135deg, ${themeColor} 0%, #5bbce4 100%)`
                                                        }}>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", py: 2, fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '25%' }}>Job Details</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '12%' }}>Location</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '12%' }}>Experience</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '12%' }}>Posted</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '12%' }}>Applicants</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', width: '10%' }}>Status</TableCell>
                                                            <TableCell sx={{ fontWeight: 800, color: "#fff", fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase', textAlign: 'center', width: '8%' }}>Action</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {jobs.length > 0 ? (
                                                            jobs.map((job, index) => (
                                                                <TableRow
                                                                    hover
                                                                    key={job._id}
                                                                    sx={{
                                                                        "&:last-child td, &:last-child th": { border: 0 },
                                                                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                                                        transition: "all 0.3s ease",
                                                                        '&:hover': {
                                                                            backgroundColor: "#eff6ff",
                                                                            transform: "scale(1.005)",
                                                                            boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell sx={{ py: 2 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                            <Box sx={{
                                                                                width: 40,
                                                                                height: 40,
                                                                                borderRadius: "10px",
                                                                                background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}30 100%)`,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                flexShrink: 0
                                                                            }}>
                                                                                <WorkIcon sx={{ fontSize: 20, color: themeColor }} />
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{job.job_title}</Typography>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                                                    <BusinessIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                                                                                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>{job.company_name}</Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <LocationOnIcon sx={{ fontSize: 14, color: "#ef4444", flexShrink: 0 }} />
                                                                            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.job_location}</Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={job.experience_type || `${job.experience_min}-${job.experience_max} Yrs`}
                                                                            size="small"
                                                                            sx={{
                                                                                borderRadius: "8px",
                                                                                backgroundColor: "#f3e8ff",
                                                                                color: "#7c3aed",
                                                                                fontWeight: 700,
                                                                                fontSize: '0.7rem',
                                                                                border: "1px solid #e9d5ff"
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <CalendarTodayIcon sx={{ fontSize: 14, color: "#10b981" }} />
                                                                            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500 }}>{job.createdDate || moment(job.created).format('MMM Do YYYY')}</Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.8,
                                                                            px: 1.5,
                                                                            py: 0.5,
                                                                            borderRadius: "8px",
                                                                            backgroundColor: "#eff6ff",
                                                                            border: "1px solid #bfdbfe"
                                                                        }}>
                                                                            <PeopleIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                                                                            <Typography variant="body2" fontWeight={800} color="#1e40af">{job.applicantCount || 0}</Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            label={job.job_status}
                                                                            size="small"
                                                                            sx={{
                                                                                borderRadius: "8px",
                                                                                px: 1,
                                                                                fontWeight: 800,
                                                                                textTransform: 'uppercase',
                                                                                fontSize: '0.65rem',
                                                                                letterSpacing: 0.5,
                                                                                background: job.job_status === "Active"
                                                                                    ? "linear-gradient(135deg, #10b981 0%, #34d399 100%)"
                                                                                    : "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
                                                                                color: "#fff",
                                                                                boxShadow: job.job_status === "Active"
                                                                                    ? "0 3px 8px rgba(16, 185, 129, 0.3)"
                                                                                    : "0 3px 8px rgba(239, 68, 68, 0.3)"
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Tooltip title="View Details" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleViewDetails(job)}
                                                                                sx={{
                                                                                    background: `linear-gradient(135deg, ${themeColor} 0%, #5bbce4 100%)`,
                                                                                    color: "#fff",
                                                                                    boxShadow: `0 4px 12px ${themeColor}40`,
                                                                                    transition: "all 0.3s ease",
                                                                                    '&:hover': {
                                                                                        transform: "scale(1.15)",
                                                                                        boxShadow: `0 6px 16px ${themeColor}60`
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <VisibilityIcon sx={{ fontSize: 18 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        <Box sx={{
                                                                            width: 60,
                                                                            height: 60,
                                                                            borderRadius: "16px",
                                                                            background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            mb: 2
                                                                        }}>
                                                                            <WorkIcon sx={{ fontSize: 28, color: "#94a3b8" }} />
                                                                        </Box>
                                                                        <Typography variant="subtitle1" sx={{ color: "#475569", fontWeight: 700, mb: 0.5 }}>No Jobs Found</Typography>
                                                                        <Typography variant="body2" sx={{ color: "#94a3b8" }}>Try adjusting your filters</Typography>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Grid container spacing={3}>
                                                {jobs.length > 0 ? (
                                                    jobs.map((job, index) => (
                                                        <Grid item xs={12} sm={6} md={showFilters ? 6 : 4} key={job._id}>
                                                            <Card
                                                                elevation={0}
                                                                sx={{
                                                                    height: '100%',
                                                                    borderRadius: "20px",
                                                                    overflow: "hidden",
                                                                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    backgroundColor: "#ffffff",
                                                                    position: 'relative',
                                                                    border: "1px solid #e2e8f0",
                                                                    '&:hover': {
                                                                        transform: "translateY(-10px) scale(1.02)",
                                                                        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
                                                                        '& .card-gradient': {
                                                                            height: '6px'
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {/* Top Gradient Accent */}
                                                                <Box
                                                                    className="card-gradient"
                                                                    sx={{
                                                                        height: '4px',
                                                                        background: job.need_type === "Urgent"
                                                                            ? 'linear-gradient(90deg, #ef4444 0%, #f97316 50%, #eab308 100%)'
                                                                            : `linear-gradient(90deg, ${themeColor} 0%, #5bbce4 50%, #a78bfa 100%)`,
                                                                        transition: 'height 0.3s ease'
                                                                    }}
                                                                />

                                                                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                                                                    {/* Status & Priority Badges */}
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                                        <Chip
                                                                            label={job.job_status}
                                                                            size="small"
                                                                            sx={{
                                                                                borderRadius: "8px",
                                                                                height: "24px",
                                                                                px: 1.5,
                                                                                fontSize: "0.65rem",
                                                                                fontWeight: 800,
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: 0.5,
                                                                                background: job.job_status === "Active"
                                                                                    ? "linear-gradient(135deg, #10b981 0%, #34d399 100%)"
                                                                                    : "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
                                                                                color: "#fff",
                                                                                boxShadow: job.job_status === "Active"
                                                                                    ? "0 4px 12px rgba(16, 185, 129, 0.4)"
                                                                                    : "0 4px 12px rgba(239, 68, 68, 0.4)"
                                                                            }}
                                                                        />
                                                                        {job.need_type === "Urgent" && (
                                                                            <Box sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                px: 1.5,
                                                                                py: 0.5,
                                                                                borderRadius: "8px",
                                                                                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                                                                border: "1px solid #fcd34d"
                                                                            }}>
                                                                                <Typography sx={{ fontSize: "12px" }}>🔥</Typography>
                                                                                <Typography variant="caption" sx={{ fontWeight: 800, color: "#b45309", textTransform: 'uppercase', fontSize: '0.6rem' }}>Urgent</Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>

                                                                    {/* Job Title */}
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{
                                                                            fontWeight: 800,
                                                                            color: "#0f172a",
                                                                            mb: 0.5,
                                                                            lineHeight: 1.3,
                                                                            fontSize: "1.05rem",
                                                                            height: '2.6em',
                                                                            overflow: 'hidden',
                                                                            display: '-webkit-box',
                                                                            WebkitLineClamp: 2,
                                                                            WebkitBoxOrient: 'vertical'
                                                                        }}
                                                                    >
                                                                        {job.job_title}
                                                                    </Typography>

                                                                    {/* Company with Icon */}
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        mb: 2,
                                                                        p: 1,
                                                                        borderRadius: "10px",
                                                                        backgroundColor: "#f8fafc"
                                                                    }}>
                                                                        <Box sx={{
                                                                            width: 30,
                                                                            height: 30,
                                                                            borderRadius: "8px",
                                                                            background: `linear-gradient(135deg, ${themeColor}20 0%, ${themeColor}40 100%)`,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}>
                                                                            <BusinessIcon sx={{ fontSize: 18, color: themeColor }} />
                                                                        </Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#334155" }}>{job.company_name}</Typography>
                                                                    </Box>

                                                                    {/* Job Details Grid */}
                                                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <LocationOnIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                                                                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>{job.job_location}</Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <WorkIcon sx={{ fontSize: 16, color: "#8b5cf6" }} />
                                                                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>{job.experience_type || `${job.experience_min}-${job.experience_max} Yrs`}</Typography>
                                                                        </Box>
                                                                    </Box>

                                                                    {/* Stats Row - Applicants & Openings */}
                                                                    <Box sx={{
                                                                        mt: 2,
                                                                        display: 'grid',
                                                                        gridTemplateColumns: '1fr 1fr',
                                                                        gap: 1.5
                                                                    }}>
                                                                        {/* Applicants Badge */}
                                                                        <Box sx={{
                                                                            p: 1.5,
                                                                            borderRadius: "10px",
                                                                            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                                                                            border: "1px solid #bfdbfe",
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                                <PeopleIcon sx={{ fontSize: 16, color: "#2563eb" }} />
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: "#1e40af", fontSize: '0.65rem' }}>Applicants</Typography>
                                                                            </Box>
                                                                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e40af", lineHeight: 1 }}>{job.applicantCount || 0}</Typography>
                                                                        </Box>
                                                                        {/* Openings Badge */}
                                                                        <Box sx={{
                                                                            p: 1.5,
                                                                            borderRadius: "10px",
                                                                            background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                                                            border: "1px solid #bbf7d0",
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                                <WorkIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: "#166534", fontSize: '0.65rem' }}>Openings</Typography>
                                                                            </Box>
                                                                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#166534", lineHeight: 1 }}>{job.number_of_positions || 0}</Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </CardContent>

                                                                {/* Action Button */}
                                                                <Box sx={{ p: 2, pt: 0 }}>
                                                                    <Button
                                                                        fullWidth
                                                                        variant="contained"
                                                                        onClick={() => handleViewDetails(job)}
                                                                        endIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                                                        sx={{
                                                                            borderRadius: "10px",
                                                                            py: 1,
                                                                            textTransform: "none",
                                                                            fontWeight: 700,
                                                                            fontSize: "0.85rem",
                                                                            background: `linear-gradient(135deg, ${themeColor} 0%, #5bbce4 100%)`,
                                                                            boxShadow: `0 8px 24px -4px ${themeColor}50`,
                                                                            '&:hover': {
                                                                                background: `linear-gradient(135deg, #287fa4 0%, ${themeColor} 100%)`,
                                                                                boxShadow: `0 12px 28px -4px ${themeColor}70`,
                                                                                transform: "translateY(-2px)"
                                                                            }
                                                                        }}
                                                                    >
                                                                        View Details
                                                                    </Button>
                                                                </Box>
                                                            </Card>
                                                        </Grid>
                                                    ))
                                                ) : (
                                                    <Grid item xs={12}>
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                textAlign: 'center',
                                                                py: 12,
                                                                borderRadius: "24px",
                                                                border: "2px dashed #e2e8f0",
                                                                backgroundColor: "#fafbfc"
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 80,
                                                                height: 80,
                                                                borderRadius: "20px",
                                                                background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mx: 'auto',
                                                                mb: 3
                                                            }}>
                                                                <WorkIcon sx={{ fontSize: 40, color: "#94a3b8" }} />
                                                            </Box>
                                                            <Typography variant="h6" sx={{ color: "#475569", fontWeight: 800, mb: 1 }}>No Jobs Found</Typography>
                                                            <Typography variant="body2" sx={{ color: "#94a3b8", maxWidth: 300, mx: 'auto' }}>
                                                                Try adjusting your search criteria or filters to find more results.
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, mt: 3 }}>
                                            <TablePagination
                                                component="div"
                                                count={totalCount}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                rowsPerPageOptions={isMobile ? [] : [10, 15, 25, 50]}
                                                labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
                                                sx={{
                                                    '& .MuiTablePagination-selectLabel': {
                                                        color: "text.secondary",
                                                        display: { xs: 'none', sm: 'block' }
                                                    },
                                                    '& .MuiTablePagination-displayedRows': {
                                                        color: "text.secondary",
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    },
                                                    '& .MuiTablePagination-select': {
                                                        display: { xs: 'none', sm: 'block' }
                                                    },
                                                    '& .MuiTablePagination-actions': {
                                                        marginLeft: { xs: 0, sm: 2 }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AdminAllJobs;

